import React from 'react';

// EmptyValue - the one marker for a missing value in a table or a detail view (W-L4).
//
// Screens used a hyphen, an em dash, "N/A" or simply nothing, so the same "no value" read four
// different ways and a blank cell could not be told apart from one still loading. The marker is
// a muted en dash that a screen reader announces as "None".
//
// Not for:
//   - a required value that is missing - keep the red "Missing" badge there (Projects Job #);
//   - "N/A" that is a real, selectable value (HE vehicle type, PCR report type);
//   - zero, which is a value and renders as 0;
//   - CSV/Excel exports, which leave missing values blank.

/** The visible glyph. Exported for string-only contexts (a `title`, a <select> option). */
export const EMPTY_MARK = '–';

export interface EmptyValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** What a screen reader announces. Defaults to "None". */
  label?: string;
}

export const EmptyValue: React.FC<EmptyValueProps> = ({ label = 'None', className = '', ...rest }) => (
  <span
    data-empty-value=""
    className={`tw-text-content-muted ${className}`}
    {...rest}
  >
    <span aria-hidden="true">{EMPTY_MARK}</span>
    <span className="tw-sr-only">{label}</span>
  </span>
);

/** True for null, undefined, '' and whitespace-only strings. Zero and false are values. */
export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Render a value, or the shared empty marker when there is none.
 *
 *   {displayValue(task.createdBy?.name)}
 *   {displayValue(row.cost, (n) => formatCurrency(n))}
 */
export function displayValue<T>(
  value: T | null | undefined,
  format?: (value: T) => React.ReactNode,
): React.ReactNode {
  if (isEmptyValue(value)) return <EmptyValue />;
  return format ? format(value as T) : (value as unknown as React.ReactNode);
}

export default EmptyValue;
